import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiChartAttribute,
} from "@incendium/api";
import { Box, Chip, Link as MuiLink, Typography } from "@mui/material";
import HeaderAvatar from "Components/Header/HeaderAvatar";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import AnalyticsCell from "features/analytics/components/AnalyticsCell";
import {
  formatCustomerCellLabel,
  formatOptionalText,
} from "features/analytics/utils/customerDisplay";
import { TTableCustomCellRender } from "features/analytics/types/types";
import { TChartData } from "Interfaces";
import { useSelectedProject } from "Hooks";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useIsMobile } from "Hooks/useIsMobile";
import { useFromToContext } from "Providers/FromToProvider";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { exportStandardChart } from "features/chartLibrary/services/chartService";
import { parseIChartToChartPayload } from "features/chartLibrary/utils";
import { base64ToArrayBuffer, downloadCsv } from "Helpers/download";

const CUSTOMER_EMAIL_FIELD = "customer_email";
const CUSTOMER_STATUS_FIELD = "customer_status";

const RFM_SEGMENT_STYLES: Record<string, { bgcolor: string; color: string }> = {
  Champions: { bgcolor: "#E8F5E9", color: "#2E7D32" },
  "High-Value Repeater": { bgcolor: "#E8F5E9", color: "#2E7D32" },
  "Loyal Customers": { bgcolor: "#FFF3E0", color: "#E65100" },
  "Loyal Customer": { bgcolor: "#FFF3E0", color: "#E65100" },
  "New Customers": { bgcolor: "#ECEFF1", color: "#546E7A" },
  "One-and-Done": { bgcolor: "#ECEFF1", color: "#546E7A" },
  "Potential Loyalists": { bgcolor: "#F3E5F5", color: "#7B1FA2" },
  "Fast Repeater": { bgcolor: "#F3E5F5", color: "#7B1FA2" },
  Promising: { bgcolor: "#EDE7F6", color: "#5E35B1" },
  "Need Attention": { bgcolor: "#FFF8E1", color: "#F57F17" },
  "Customers Needing Attention": { bgcolor: "#FFF8E1", color: "#F57F17" },
  "Needs Attention": { bgcolor: "#FFF8E1", color: "#F57F17" },
  "At Risk": { bgcolor: "#FFEBEE", color: "#C62828" },
  Hibernating: { bgcolor: "#F5F5F5", color: "#757575" },
  Dormant: { bgcolor: "#F5F5F5", color: "#757575" },
  "Can't Lose Them": { bgcolor: "#FFEBEE", color: "#C62828" },
  "About To Sleep": { bgcolor: "#FFF3E0", color: "#E65100" },
};

function CustomerStatusChip({ status }: { status: string }) {
  const styles = RFM_SEGMENT_STYLES[status] || {
    bgcolor: "rgba(0, 0, 0, 0.06)",
    color: "inherit",
  };

  return (
    <Chip
      label={status || "-"}
      size="small"
      sx={{
        bgcolor: styles.bgcolor,
        color: styles.color,
        fontWeight: 600,
        borderRadius: 999,
        height: 28,
      }}
    />
  );
}

function analyticsCellProps(params: {
  data?: TChartData & { name?: string };
  colDef?: { headerName?: string };
}) {
  return {
    dimensionName: String(params.data?.name || ""),
    headerName: String(params.colDef?.headerName || ""),
    rowFilters: [],
  };
}

interface ICustomerRetentionTableProps {
  attributes?: ApiChartAttribute[];
}

function CustomerRetentionTable({
  attributes = [],
}: ICustomerRetentionTableProps) {
  const { chartOutput, calcNumberOfDays } = useFromToContext();
  const { selectedProject } = useSelectedProject();
  const { pathByName } = useProtectedRoutes();
  const isMobile = useIsMobile();

  const getUserDashboardPath = useCallback(
    (leadId: number) => {
      if (!leadId || !selectedProject?.id || !selectedProject?.clientId) {
        return "";
      }

      return pathByName(RouteNames.USER_DASHBOARD_SINGLE, {
        clientId: selectedProject.clientId,
        projectId: selectedProject.id,
        leadid: leadId,
      });
    },
    [pathByName, selectedProject?.clientId, selectedProject?.id],
  );

  const activitySubtitle = useMemo(() => {
    if (!chartOutput) {
      return "Customers with activity in the selected period";
    }

    const days = calcNumberOfDays(chartOutput);
    if (days <= 0) {
      return "Customers with activity in the selected period";
    }

    return `Customers with activity in the last ${days} days`;
  }, [calcNumberOfDays, chartOutput]);

  const tableHeader = useMemo(
    () => (
      <Box>
        <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
          Customers
        </Typography>
        <Typography
          variant="body2"
          color="secondary"
          sx={{ lineHeight: 1.2 }}
          fontWeight={500}
        >
          {activitySubtitle}
        </Typography>
      </Box>
    ),
    [activitySubtitle],
  );

  const customDataFn = useCallback(
    (data: TChartData[]) =>
      data.map((row) => ({
        [ApiDimension.DIMENSION_LEAD_ID]: Number(
          row[ApiDimension.DIMENSION_LEAD_ID],
        ),
        [ApiDimension.DIMENSION_NAME]: row[ApiDimension.DIMENSION_NAME],
        [ApiDimension.DIMENSION_LEAD_KEY]: row[ApiDimension.DIMENSION_LEAD_KEY],
        [CUSTOMER_EMAIL_FIELD]: formatOptionalText(
          row[ApiDimension.DIMENSION_EMAIL_ADDRESS],
        ),
        [ApiDimension.DIMENSION_FIRST_SALE_DATE]:
          row[ApiDimension.DIMENSION_FIRST_SALE_DATE],
        [ApiMetric.METRIC_SALES_COUNT]: row[ApiMetric.METRIC_SALES_COUNT],
        [ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE]:
          row[ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE],
        [ApiMetric.METRIC_LEAD_LIFETIME_REVENUE]:
          row[ApiMetric.METRIC_LEAD_LIFETIME_REVENUE],
        [ApiMetric.METRIC_AVERAGE_SALE_VALUE]:
          row[ApiMetric.METRIC_AVERAGE_SALE_VALUE],
        [CUSTOMER_STATUS_FIELD]: row[ApiDimension.DIMENSION_RFM_SEGMENT],
        name: row.name,
        tick: row.tick,
      })),
    [],
  );

  const tableCustomHeaderRender = useMemo<TTableCustomCellRender>(
    () => ({
      [ApiDimension.DIMENSION_LEAD_ID]: () => <>Customer</>,
      [CUSTOMER_EMAIL_FIELD]: () => <>Email</>,
      [ApiDimension.DIMENSION_FIRST_SALE_DATE]: () => <>First Order Date</>,
      [ApiMetric.METRIC_SALES_COUNT]: () => <>Orders</>,
      [ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE]: () => (
        <>Days to 2nd Order</>
      ),
      [ApiMetric.METRIC_LEAD_LIFETIME_REVENUE]: () => <>Lifetime Revenue</>,
      [ApiMetric.METRIC_AVERAGE_SALE_VALUE]: () => <>Avg Order Value</>,
      [CUSTOMER_STATUS_FIELD]: () => <>Customer Status</>,
    }),
    [],
  );

  const tableCustomCellRender = useMemo<TTableCustomCellRender>(
    () => ({
      [ApiDimension.DIMENSION_LEAD_ID]: ({ value, data }) => {
        const leadId = Number(value);
        const name = formatCustomerCellLabel(
          data?.[ApiDimension.DIMENSION_NAME],
          data?.[ApiDimension.DIMENSION_LEAD_KEY],
        );
        const href = getUserDashboardPath(leadId) || undefined;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              height: "100%",
            }}
          >
            <HeaderAvatar name={name} size="medium" variant="light" />
            {href ? (
              <MuiLink
                component={Link}
                to={href}
                underline="hover"
                sx={{ fontWeight: 600, fontSize: "13px" }}
              >
                {name}
              </MuiLink>
            ) : (
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontSize: "13px" }}
              >
                {name}
              </Typography>
            )}
          </Box>
        );
      },
      [CUSTOMER_EMAIL_FIELD]: (params) => (
        <AnalyticsCell
          {...analyticsCellProps(params)}
          valueFormatted={formatOptionalText(params.value)}
          isDimension
        />
      ),
      [CUSTOMER_STATUS_FIELD]: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <CustomerStatusChip status={String(value || "")} />
        </Box>
      ),
    }),
    [getUserDashboardPath],
  );

  const chart = useMemo(
    () => ({
      name: "Customer Retention",
      dimension: [
        ApiDimension.DIMENSION_LEAD_ID,
        ApiDimension.DIMENSION_NAME,
        ApiDimension.DIMENSION_LEAD_KEY,
        ApiDimension.DIMENSION_EMAIL_ADDRESS,
        ApiDimension.DIMENSION_FIRST_SALE_DATE,
        ApiDimension.DIMENSION_RFM_SEGMENT,
      ],
      template: ApiChartTemplate.TABLE,
      type: ApiChartType.TABLE,
      dateDimension: ApiDimension.DIMENSION_SESSION_START_BY_DAY,
      orderBy: `${ApiDimension.DIMENSION_FIRST_SALE_DATE} DESC`,
      attributes,
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE,
            ApiMetric.METRIC_SALES_COUNT,
            ApiMetric.METRIC_LEAD_LIFETIME_REVENUE,
            ApiMetric.METRIC_AVERAGE_SALE_VALUE,
          ],
        },
      ],
      includeEmpty: true,
      having: [
        {
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_GREATER_THAN,
          value: 0,
        },
      ],
      displayOptions: {
        noAttributionToggle: true,
        noFilters: true,
        noTitle: true,
        disabledDimensionClick: true,
        disabledMetricClick: true,
        rowsPerPage: 0,
        tableColWidths: {
          [ApiDimension.DIMENSION_LEAD_ID]: isMobile ? 150 : 220,
          [CUSTOMER_EMAIL_FIELD]: isMobile ? 125 : 240,
          [ApiDimension.DIMENSION_FIRST_SALE_DATE]: isMobile ? 125 : 150,
          [ApiMetric.METRIC_SALES_COUNT]: isMobile ? 100 : 100,
          [ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE]: isMobile
            ? 100
            : 140,
          [ApiMetric.METRIC_LEAD_LIFETIME_REVENUE]: isMobile ? 100 : 150,
          [ApiMetric.METRIC_AVERAGE_SALE_VALUE]: isMobile ? 100 : 140,
          [CUSTOMER_STATUS_FIELD]: isMobile ? 125 : 160,
        },
      },
    }),
    [isMobile, attributes],
  );

  const onChartExport = useCallback(async () => {
    if (!selectedProject?.id) {
      return;
    }
    let copyEdChart = { ...chart };
    copyEdChart.dimension = [
      ApiDimension.DIMENSION_NAME,
      ApiDimension.DIMENSION_LEAD_KEY,
      ApiDimension.DIMENSION_EMAIL_ADDRESS,
      ApiDimension.DIMENSION_FIRST_SALE_DATE,
      ApiDimension.DIMENSION_RFM_SEGMENT,
    ];
    const res = await exportStandardChart(
      selectedProject?.id,
      parseIChartToChartPayload(copyEdChart),
      {
        ...chartOutput,
      },
    );

    if (res?.result?.result) {
      const buffer = base64ToArrayBuffer(res.result.result);
      downloadCsv(buffer, chart.name || "Chart Export");
    }
  }, [selectedProject?.id, chart, chartOutput]);

  return (
    <AnalyticsCard
      ignoreAttrModel
      forceNoComparison
      customHeader={tableHeader}
      customDataFn={customDataFn}
      tableCustomCellRender={tableCustomCellRender}
      tableCustomHeaderRender={tableCustomHeaderRender}
      onExport={onChartExport}
      tableHiddenCols={[
        ApiDimension.DIMENSION_NAME,
        ApiDimension.DIMENSION_LEAD_KEY,
      ]}
      tableUnpinnedCols={[
        ApiDimension.DIMENSION_FIRST_SALE_DATE,
        ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE,
      ]}
      tableHeaderAlign={{
        [ApiDimension.DIMENSION_LEAD_ID]: "left",
        [CUSTOMER_EMAIL_FIELD]: "left",
        [ApiDimension.DIMENSION_FIRST_SALE_DATE]: "left",
        [ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE]: "left",
        [ApiMetric.METRIC_LEAD_LIFETIME_REVENUE]: "center",
        [CUSTOMER_STATUS_FIELD]: "center",
      }}
      chart={chart}
    />
  );
}

export default CustomerRetentionTable;
